import { useQuery } from '@tanstack/react-query'
import { useApi } from '../api/contexto'
import type { ErrorApi } from '../api/errores'

/** Perfil 360º de un cliente (GET /api/clientes/:id/perfil). */
export interface PerfilCliente {
  contactId: string
  nombre: string
  telefono: string | null
  direccion: string | null
  codigoPostal: string | null
  poblacion: string | null
  provincia: string | null
  latitud: number | null
  longitud: number | null
  etiquetas: string[]
  estadoCiclo: string | null
  aplazamientos: number
  activo: boolean
  /** Interruptor de avisos: false = no recibe el aviso del lunes (Iván oct-2026). */
  avisar: boolean
  /** Fecha hasta la que está aplazado (no se le avisa). null = sin aplazamiento. */
  aplazadoHasta: string | null
  ultimosRepartos: string[]
  albaranes: AlbaranDetalle[]
  respuestaSemana: string | null
  respondioSemana: boolean
  enlaceChat: string | null
}

/** Una línea de un albarán (producto entregado). */
export interface LineaAlbaran {
  nombre: string
  unidades: number
  precio: number
}

/** Un albarán con su detalle, para el historial desplegable del perfil. */
export interface AlbaranDetalle {
  fecha: string
  lineas: LineaAlbaran[]
  total: number
}

/**
 * Perfil de un cliente por contactId. `enabled` controla la carga: el panel se
 * monta con el id null hasta que se pide abrir, así no dispara la consulta (ni
 * el golpe a Holded del historial) hasta que de verdad se necesita.
 */
export function usePerfilCliente(contactId: string | null) {
  const api = useApi()
  return useQuery<PerfilCliente, ErrorApi>({
    queryKey: ['perfil-cliente', contactId],
    enabled: !!contactId,
    queryFn: ({ signal }) =>
      api.pedir(`/api/clientes/${encodeURIComponent(contactId as string)}/perfil`, { senal: signal }),
    staleTime: 60_000,
  })
}

/**
 * Perfil de un cliente por teléfono (para abrirlo desde un chat de cliente en
 * Conversaciones, donde se conoce el número pero no el contactId).
 */
export function usePerfilPorTelefono(telefono: string | null) {
  const api = useApi()
  return useQuery<PerfilCliente, ErrorApi>({
    queryKey: ['perfil-cliente-tel', telefono],
    enabled: !!telefono,
    queryFn: ({ signal }) =>
      api.pedir(`/api/clientes/perfil/por-telefono/${encodeURIComponent(telefono as string)}`, { senal: signal }),
    staleTime: 60_000,
  })
}

/**
 * Carga más albaranes de un cliente bajo demanda (botón "Cargar más" del
 * historial). `enabled` solo cuando se pide; no golpea Holded al abrir el perfil.
 */
export function useAlbaranesMas(contactId: string, offset: number, activo: boolean) {
  const api = useApi()
  return useQuery<{ items: AlbaranDetalle[]; offset: number }, ErrorApi>({
    queryKey: ['albaranes-mas', contactId, offset],
    enabled: activo && !!contactId && offset > 0,
    queryFn: ({ signal }) =>
      api.pedir(`/api/clientes/${encodeURIComponent(contactId)}/albaranes?offset=${offset}`, { senal: signal }),
    staleTime: 60_000,
  })
}
